import { AgBytesAPI } from "@ai360/core";
import { useEffect, useMemo } from "react";
import { createSelector } from "reselect";

import {
    computeIsSelectable,
    getLoggedInUserProductScopeOrgLevelGuid,
} from "~/utils/products/use-product-list";
import { messages as globalMessages } from "~/i18n-messages";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

const EMPTY_CROPS: AgBytesAPI.ICropSelectionListItem[] = [];
const EMPTY_GUIDS: string[] = [];

export interface IUseCropListOptions {
    customerGuid?: string | null;
    isEnabled?: boolean;
    orgLevelGuid?: string | null;
    /** Multiple Location Affiliations -- fetches each location and unions the results by
     * `cropGuid`. Takes precedence over `orgLevelGuid`/`customerGuid` when non-empty. */
    orgLevelGuids?: (string | null | undefined)[];
}

export interface IUseCropListResult {
    crops: AgBytesAPI.ICropSelectionListItem[];
    isFetching: boolean;
}

/**
 * Picks one row per `cropGuid` across locations rather than merging fields piecemeal --
 * `agvanceNoPurposes`/`agvancePurposes` only make sense read off the same row `isAgvanceLinked` came
 * from. Prefers a location where the crop is linked, then one where linkage is unresolved, and
 * only falls back to a `false` row when every location agrees it's unlinked. `linkageCheckDegraded`
 * still merges independently -- it's useful to know a check was degraded somewhere even when a
 * different location resolved the crop cleanly.
 */
const mergeCropLists = (
    resultsByLocation: AgBytesAPI.ICropSelectionListItem[][]
): AgBytesAPI.ICropSelectionListItem[] => {
    const rowsByGuid = new Map<string, AgBytesAPI.ICropSelectionListItem[]>();
    for (const rows of resultsByLocation) {
        for (const row of rows) {
            rowsByGuid.set(row.cropGuid, [...(rowsByGuid.get(row.cropGuid) ?? []), row]);
        }
    }
    return Array.from(rowsByGuid.values()).map((rows) => {
        const picked =
            rows.find((row) => row.isAgvanceLinked === true) ??
            rows.find((row) => row.isAgvanceLinked === null) ??
            rows[0];
        return {
            ...picked,
            linkageCheckDegraded: rows.some((row) => row.linkageCheckDegraded),
        };
    });
};

/**
 * One RTK cache entry over `POST agbytes/v1.0/crop`. Use this instead of a per-surface
 * fetch, so identical arguments share one request.
 */
export const useCropList = ({
    customerGuid = null,
    isEnabled = true,
    orgLevelGuid = null,
    orgLevelGuids,
}: IUseCropListOptions): IUseCropListResult => {
    const dispatch = useAppDispatch();
    const isMultiLocation = orgLevelGuids != null;

    const isScopeResolvable = Boolean(customerGuid || orgLevelGuid);
    const { data: crops = EMPTY_CROPS, isFetching: isSingleFetching } =
        AgBytesAPI.useGetCropSelectionListQuery(
            {
                ...(customerGuid && { customerGuid }),
                ...(!customerGuid && orgLevelGuid != null && { orgLevelGuid }),
            },
            {
                skip: isMultiLocation || !isEnabled || !isScopeResolvable,
                refetchOnMountOrArgChange: true,
            }
        );

    const guidList = useMemo(
        () =>
            !isMultiLocation
                ? EMPTY_GUIDS
                : Array.from(
                      new Set(orgLevelGuids.filter((guid): guid is string => Boolean(guid)))
                  ).sort(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMultiLocation, (orgLevelGuids ?? []).join("|")]
    );

    useEffect(() => {
        if (!isMultiLocation || !isEnabled || guidList.length === 0) {
            return;
        }
        const subscriptions = guidList.map((guid) =>
            dispatch(
                AgBytesAPI.extendedAi360Api.endpoints.getCropSelectionList.initiate({
                    orgLevelGuid: guid,
                })
            )
        );
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [dispatch, isMultiLocation, isEnabled, guidList]);

    const multiLocationResult = useAppSelector((state) => {
        if (!isMultiLocation || guidList.length === 0) {
            return { data: EMPTY_CROPS, isFetching: false };
        }
        const selections = guidList.map((guid) =>
            AgBytesAPI.extendedAi360Api.endpoints.getCropSelectionList.select({
                orgLevelGuid: guid,
            })(state)
        );
        const anyPending = selections.some(
            (selection) => selection.isFetching || selection.isUninitialized
        );
        // Don't publish a partial union while any location is still in flight -- a crop
        // linked only at a not-yet-resolved location would otherwise read as unlinked for the
        // brief window before every leg settles.
        return {
            data: anyPending
                ? EMPTY_CROPS
                : mergeCropLists(selections.map((selection) => selection.data ?? EMPTY_CROPS)),
            isFetching: anyPending,
        };
    });

    return {
        crops: isMultiLocation ? multiLocationResult.data : crops,
        isFetching: isMultiLocation ? multiLocationResult.isFetching : isSingleFetching,
    };
};

export interface ICropDropdownOption {
    activeYn: boolean;
    /** Linked rows with no purpose, kept per row (see `pickAgvanceSources`). Null unless linked. */
    agvanceNoPurposes: AgBytesAPI.ICropSelectionListItem["agvanceNoPurposes"];
    /** Full linked-purpose/chemistry detail -- steps 2-3 of the color ladder, plus Chemistry rescoping. Null unless linked. */
    agvancePurposes: AgBytesAPI.ICropSelectionListItem["agvancePurposes"];
    defaultMapColor: string;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useCropList()` result to the plain option shape crop consumers expect (the same shape
 * `AgBytesItem`/legacy `cropData` supplied: `guid`, `id`, `name`, `activeYn`, `defaultMapColor`), plus
 * `isAgvanceLinked` for visibility and `agvanceNoPurposes`/`agvancePurposes` for the color ladder and
 * crop/purpose-scoped Chemistry (`analysis-management-area-form.tsx`).
 */
export const toCropDropdownOptions = (
    crops: AgBytesAPI.ICropSelectionListItem[]
): ICropDropdownOption[] =>
    crops.map((crop) => ({
        activeYn: crop.activeYn,
        agvanceNoPurposes: crop.agvanceNoPurposes,
        agvancePurposes: crop.agvancePurposes,
        defaultMapColor: crop.defaultMapColor,
        guid: crop.cropGuid,
        id: String(crop.cropId),
        isAgvanceLinked: crop.isAgvanceLinked,
        name: crop.name,
    }));

/**
 * `mapStateToProps` equivalent of `useCropList()` + `toCropDropdownOptions()`, for the class
 * components that can't call the hook directly.
 */
const getCropListData = (state: unknown): AgBytesAPI.ICropSelectionListItem[] => {
    const orgLevelGuid = getLoggedInUserProductScopeOrgLevelGuid(state);
    return (
        AgBytesAPI.extendedAi360Api.endpoints.getCropSelectionList.select(
            orgLevelGuid ? { orgLevelGuid } : {}
        )(state)?.data ?? EMPTY_CROPS
    );
};
export const getCropData = createSelector(getCropListData, toCropDropdownOptions);

export interface ILinkageStampable {
    activeYn: boolean;
    isAgvanceLinked: boolean | null;
}

/**
 * Minimal shape these helpers actually call `formatMessage` with: one descriptor in, a plain
 * string out — narrower than `IntlShape["formatMessage"]` (which also takes values/opts and
 * can return `ReactNode[]`) so callers with a looser local prop type (e.g.
 * `RestrictedCropList`'s `(descriptor: unknown) => string`) stay assignable. Same
 * declare-what's-used move as `isProductUnlinked` in `blending-utils.tsx`; confirmed against
 * real call sites via `npm run typecheck:full`. Shared with `use-crop-purpose-list.ts`.
 */
export type FormatCropTooltipMessage = (descriptor: {
    id: string;
    defaultMessage: string;
}) => string;

/**
 * Stamps `isSelectable` (and, given `formatMessage`, an unlinked-crop `getToolTip`) onto any
 * option list carrying `activeYn`/`isAgvanceLinked` — not just raw crop rows, so a site with
 * its own already-shaped `{value, label, ...}` options (e.g. `RestrictedCropList`) can stamp
 * them too, not only the `toCropDropdownOptions()` pipeline below.
 */
export const stampCropSelectable = <T extends ILinkageStampable>(
    options: T[],
    formatMessage?: FormatCropTooltipMessage
): (T & { isSelectable: boolean; getToolTip?: () => string })[] =>
    options.map((option) => ({
        ...option,
        isSelectable: computeIsSelectable(option.activeYn, option.isAgvanceLinked),
        ...(formatMessage &&
            option.isAgvanceLinked === false && {
                getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
            }),
    }));

/**
 * Stamps `isSelectable` (and, given `formatMessage`, an unlinked-crop `getToolTip`) onto
 * `toCropDropdownOptions()`'s result so a `SelectInput` can key `optionIsHiddenKey` off it.
 */
export const toSelectableCropDropdownOptions = (
    crops: AgBytesAPI.ICropSelectionListItem[],
    formatMessage?: FormatCropTooltipMessage
): (ICropDropdownOption & { isSelectable: boolean; getToolTip?: () => string })[] =>
    stampCropSelectable(toCropDropdownOptions(crops), formatMessage);

export interface ICropSelectOption {
    activeYn: boolean;
    isAgvanceLinked: boolean | null;
    isSelectable: boolean;
    getToolTip?: () => string;
    label: string;
    value: string;
}

/**
 * The `{value, label}`-keyed counterpart to `toSelectableCropDropdownOptions()`, for
 * `SelectInput`-direct call sites (action-panel forms). Takes `ICropDropdownOption[]` (the
 * `guid`/`name`-keyed shape from `toCropDropdownOptions()`), not the raw
 * `AgBytesAPI.ICropSelectionListItem[]` — every action-panel form receives `cropData` already
 * converted by a parent (or `use-product-list.ts`'s class equivalent), never fetching raw
 * crops itself. Confirmed against real call sites (`event-planting-form.tsx`,
 * `analysis-profit-loss-form.tsx`, `event-scouting-template-modal.tsx`,
 * `batch-edit-events-modal.tsx`) via `npm run typecheck:full` — don't revert to the raw crop
 * type without reconfirming.
 *
 * `formatMessage` is required, not optional: every caller here wants a tooltip on an unlinked
 * crop, and making it optional would reintroduce the silent no-tooltip gap that
 * `toSelectableCropDropdownOptions` already has at most of its callers.
 */
export const toCropSelectOptions = (
    crops: ICropDropdownOption[],
    formatMessage: FormatCropTooltipMessage
): ICropSelectOption[] =>
    stampCropSelectable(
        crops.map((crop) => ({
            activeYn: crop.activeYn,
            isAgvanceLinked: crop.isAgvanceLinked,
            label: crop.name,
            value: crop.guid,
        })),
        formatMessage
    );

/**
 * Mirrors `blending-utils.tsx`'s `isProductUnlinked` -- a plain function, not a hook, so it can be
 * called from `mapStateToProps`, sagas, or a class `render()`, not just function components.
 */
export const isCropUnlinked = (
    cropGuid: string | null | undefined,
    crops: ICropDropdownOption[]
): boolean => {
    if (!cropGuid) {
        return false;
    }
    const crop = crops.find((c) => c.guid === cropGuid);
    return crop != null && crop.isAgvanceLinked === false;
};
